<script setup lang="ts">
import { reactive, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import { useConnectivity } from '@/composables/useConnectivity';

const props = defineProps<{
  mode: 'password' | 'link' | 'passkey';
}>();

const { t } = useI18n();
const { isOnline } = useConnectivity();

/** Django `EmailField` English default (allauth may return this regardless of `Accept-Language`). */
const DJANGO_EMAIL_INVALID_EN = /^Enter a valid email address\.?$/i;

/** django-allauth login failure (often English regardless of `Accept-Language`). */
const ALLAUTH_CREDENTIALS_INVALID_EN =
  /^The email address and\/or password you specified are not correct\.?$/i;

/** allauth / project login-by-code confirm (often English regardless of `Accept-Language`). */
const ALLAUTH_LOGIN_CODE_INVALID_EN = /^Incorrect code\.?$/i;

const LOGIN_CODE_EXPIRED_EN =
  /^The code is invalid or has expired\. Try requesting a new login link\.?$/i;

function translatedEmailError(raw: string): string {
  if (DJANGO_EMAIL_INVALID_EN.test(raw.trim())) {
    return t('account.signInForm.emailInvalid');
  }
  return raw;
}

function translatedPasswordError(raw: string): string {
  if (ALLAUTH_CREDENTIALS_INVALID_EN.test(raw.trim())) {
    return t('account.signInForm.credentialsInvalid');
  }
  return raw;
}

function translatedLoginCodeError(raw: string): string {
  const message = raw.trim();
  if (ALLAUTH_LOGIN_CODE_INVALID_EN.test(message)) {
    return t('account.signInForm.codeInvalid');
  }
  if (LOGIN_CODE_EXPIRED_EN.test(message)) {
    return t('account.signInForm.codeExpired');
  }
  return raw;
}

const authStore = useAuthStore();
const {
  loginLoading,
  loginErrors,
  loginCodeSent,
  loginCodeSentTo,
  loginCodeConfirmError,
  loginPendingMfa,
} = storeToRefs(authStore);

const form = reactive({
  email: '',
  password: '',
  code: '',
});

const showPassword = ref(false);
const rememberMe = ref(false);
const passkeyInfoTitleId = `${useId()}-passkey-info-title`;

watch(
  () => props.mode,
  () => {
    authStore.clearLoginState();
  }
);

function useDifferentSignInMethod() {
  authStore.clearLoginState();
}

async function onPasskeySignIn() {
  if (!isOnline.value) return;
  await authStore.loginWithPasskey();
}

async function onCompleteMfaPasskey() {
  if (!isOnline.value) return;
  await authStore.completeLoginWithMfaPasskey();
}

async function onSubmitPassword(e: Event) {
  e.preventDefault();
  if (!isOnline.value) return;
  await authStore.login({
    email: form.email,
    password: form.password,
    remember: rememberMe.value,
  });
}

async function onSubmitRequestCode(e: Event) {
  e.preventDefault();
  if (!isOnline.value) return;
  await authStore.requestLoginCode(form.email);
}

async function onSubmitCode(e: Event) {
  e.preventDefault();
  if (!isOnline.value) return;
  await authStore.confirmLoginCode(form.code);
}
</script>

<template>
  <div class="signin-form">
    <!-- MFA step (shown after any primary auth method returns mfa_authenticate pending) -->
    <div v-if="loginPendingMfa === 'webauthn'" class="signin-form__stack">
      <p v-if="loginErrors.generalError" class="auth-feedback auth-feedback--error" role="alert">
        {{ loginErrors.generalError }}
      </p>
      <div
        class="signin-form__callout signin-form__callout--info"
        role="region"
      >
        <span class="signin-form__callout-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
        </span>
        <div>
          <p class="signin-form__callout-title">{{ t('account.authCard.mfaPasskeyRequiredTitle') }}</p>
          <p class="signin-form__callout-body">{{ t('account.authCard.mfaPasskeyRequiredBody') }}</p>
          <button
            type="button"
            class="auth-btn auth-btn--solid signin-form__mfa-passkey-btn"
            :disabled="loginLoading || !isOnline"
            @click="onCompleteMfaPasskey"
          >
            {{ loginLoading ? t('account.authCard.signingIn') : t('account.authCard.continuePasskey') }}
          </button>
        </div>
      </div>
      <p class="signin-form__alt-methods">
        <button
          type="button"
          class="auth-btn auth-btn--inline"
          :disabled="loginLoading"
          @click="useDifferentSignInMethod"
        >
          {{ t('account.authCard.differentSignInMethod') }}
        </button>
      </p>
    </div>
    <div v-else-if="loginPendingMfa === 'other'" class="signin-form__stack">
      <div
        class="signin-form__callout signin-form__callout--info"
        role="alert"
      >
        <span class="signin-form__callout-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
        </span>
        <div>
          <p class="signin-form__callout-title">{{ t('account.authCard.mfaRequiredTitle') }}</p>
          <p class="signin-form__callout-body">{{ t('account.authCard.mfaOtherFactorBody') }}</p>
        </div>
      </div>
      <p class="signin-form__alt-methods">
        <button
          type="button"
          class="auth-btn auth-btn--inline"
          :disabled="loginLoading"
          @click="useDifferentSignInMethod"
        >
          {{ t('account.authCard.differentSignInMethod') }}
        </button>
      </p>
    </div>

    <!-- Password -->
    <form
      v-else-if="props.mode === 'password' && !loginCodeSent"
      class="signin-form__stack"
      @submit="onSubmitPassword"
    >
      <p v-if="loginErrors.generalError" class="auth-feedback auth-feedback--error" role="alert">
        {{ loginErrors.generalError }}
      </p>
      <div class="auth-field">
        <label for="signin-email" class="auth-label">
          {{ t('account.signInForm.emailLabel') }}
          <span class="signin-form__req" aria-hidden="true">*</span>
        </label>
        <div class="signin-form__input-wrap">
          <span class="signin-form__input-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect width="20" height="16" x="2" y="4" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
          </span>
          <input
            id="signin-email"
            v-model="form.email"
            type="email"
            name="email"
            autocomplete="email"
            required
            :disabled="loginLoading || !isOnline"
            :aria-invalid="(loginErrors.fieldErrors?.email?.length ?? 0) > 0"
            :aria-describedby="
              (loginErrors.fieldErrors?.email?.length ?? 0) ? 'signin-email-err' : undefined
            "
            class="auth-input signin-form__input--padded"
            :placeholder="t('account.signInForm.emailPlaceholder')"
          />
        </div>
        <p
          v-if="loginErrors.fieldErrors?.email?.length"
          id="signin-email-err"
          class="auth-field-error"
          role="alert"
        >
          {{ translatedEmailError(loginErrors.fieldErrors.email[0] ?? '') }}
        </p>
      </div>
      <div class="auth-field">
        <label for="signin-password" class="auth-label">
          {{ t('account.signInForm.passwordLabel') }}
          <span class="signin-form__req" aria-hidden="true">*</span>
        </label>
        <div class="signin-form__input-wrap">
          <span class="signin-form__input-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </span>
          <input
            id="signin-password"
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            name="password"
            autocomplete="current-password"
            required
            :disabled="loginLoading || !isOnline"
            :aria-invalid="(loginErrors.fieldErrors?.password?.length ?? 0) > 0"
            :aria-describedby="
              (loginErrors.fieldErrors?.password?.length ?? 0) ? 'signin-password-err' : undefined
            "
            class="auth-input signin-form__input--padded signin-form__input--password"
            :placeholder="t('account.signInForm.passwordPlaceholder')"
          />
          <button
            type="button"
            class="signin-form__toggle-pw"
            :aria-label="
              showPassword ? 'Hide password' : 'Show password'"
            :disabled="loginLoading || !isOnline"
            @click="showPassword = !showPassword"
          >
            <svg v-if="!showPassword" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
              <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
              <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
              <line x1="2" x2="22" y1="2" y2="22" />
            </svg>
          </button>
        </div>
        <p
          v-if="loginErrors.fieldErrors?.password?.length"
          id="signin-password-err"
          class="auth-field-error"
          role="alert"
        >
          {{ translatedPasswordError(loginErrors.fieldErrors.password[0] ?? '') }}
        </p>
      </div>
      <div class="signin-form__row-between">
        <label class="signin-form__remember">
          <input v-model="rememberMe" type="checkbox" class="signin-form__checkbox" :disabled="loginLoading || !isOnline" />
          <span>{{ t('account.authCard.rememberMe') }}</span>
        </label>
        <router-link class="signin-form__forgot" :to="{ name: 'password-reset-request' }">
          {{ t('account.authCard.forgotPassword') }}
        </router-link>
      </div>
      <div class="signin-form__cta-row">
        <router-link class="auth-btn auth-btn--outline signin-form__cancel" to="/">
          {{ t('account.authCard.cancel') }}
        </router-link>
        <button type="submit" class="auth-btn auth-btn--solid signin-form__submit" :disabled="loginLoading || !isOnline">
          {{ loginLoading ? t('account.authCard.signingIn') : t('account.authCard.signIn') }}
        </button>
      </div>
    </form>

    <!-- Passkey -->
    <div v-else-if="props.mode === 'passkey' && !loginCodeSent" class="signin-form__stack">
      <p v-if="loginErrors.generalError" class="auth-feedback auth-feedback--error" role="alert">
        {{ loginErrors.generalError }}
      </p>
      <p class="signin-form__muted">{{ t('account.authCard.passkeyIntro') }}</p>
      <div class="signin-form__callout signin-form__callout--info" role="region" :aria-labelledby="passkeyInfoTitleId">
        <span class="signin-form__callout-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
        </span>
        <div>
          <p :id="passkeyInfoTitleId" class="signin-form__callout-title">{{ t('account.authCard.passkeyInfoTitle') }}</p>
          <p class="signin-form__callout-body">{{ t('account.authCard.passkeyInfoBody') }}</p>
        </div>
      </div>
      <div class="signin-form__cta-row">
        <router-link class="auth-btn auth-btn--outline signin-form__cancel" to="/">
          {{ t('account.authCard.cancel') }}
        </router-link>
        <button
          type="button"
          class="auth-btn auth-btn--solid signin-form__submit"
          :disabled="loginLoading || !isOnline"
          @click="onPasskeySignIn"
        >
          {{ loginLoading ? t('account.authCard.signingIn') : t('account.authCard.continuePasskey') }}
        </button>
      </div>
    </div>

    <!-- Email link: request -->
    <form
      v-else-if="props.mode === 'link' && !loginCodeSent"
      class="signin-form__stack"
      @submit="onSubmitRequestCode"
    >
      <p v-if="loginErrors.generalError" class="auth-feedback auth-feedback--error" role="alert">
        {{ loginErrors.generalError }}
      </p>
      <p class="signin-form__muted">{{ t('account.authCard.linkIntro') }}</p>
      <div class="auth-field">
        <label for="signin-link-email" class="auth-label">
          Email <span class="signin-form__req" aria-hidden="true">*</span>
        </label>
        <div class="signin-form__input-wrap">
          <span class="signin-form__input-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect width="20" height="16" x="2" y="4" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
          </span>
          <input
            id="signin-link-email"
            v-model="form.email"
            type="email"
            name="email"
            autocomplete="email"
            required
            :disabled="loginLoading || !isOnline"
            :aria-invalid="(loginErrors.fieldErrors?.email?.length ?? 0) > 0"
            :aria-describedby="
              (loginErrors.fieldErrors?.email?.length ?? 0) ? 'signin-link-email-err' : undefined
            "
            class="auth-input signin-form__input--padded"
            :placeholder="t('account.signInForm.emailPlaceholder')"
          />
        </div>
        <p
          v-if="loginErrors.fieldErrors?.email?.length"
          id="signin-link-email-err"
          class="auth-field-error"
          role="alert"
        >
          {{ translatedEmailError(loginErrors.fieldErrors.email[0] ?? '') }}
        </p>
      </div>
      <div class="signin-form__cta-row">
        <router-link class="auth-btn auth-btn--outline signin-form__cancel" to="/">
          {{ t('account.authCard.cancel') }}
        </router-link>
        <button type="submit" class="auth-btn auth-btn--solid signin-form__submit" :disabled="loginLoading || !isOnline">
          {{ loginLoading ? t('account.authCard.sending') : t('account.authCard.sendLoginLink') }}
        </button>
      </div>
    </form>

    <!-- Email link: code -->
    <template v-else-if="loginCodeSent">
      <div class="signin-form__stack">
        <div class="signin-form__callout signin-form__callout--sent" role="status">
          <span class="signin-form__callout-icon signin-form__callout-icon--primary" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m22 2-7 20-4-9-9-4Z" />
              <path d="M22 2 11 13" />
            </svg>
          </span>
          <div>
            <p class="signin-form__callout-title signin-form__callout-title--primary">
              {{ t('account.authCard.emailSentTitle') }}
            </p>
            <p class="signin-form__callout-body">
              {{ t('account.authCard.emailSentBody') }}
              <template v-if="loginCodeSentTo">
                ({{ loginCodeSentTo }})
              </template>
            </p>
          </div>
        </div>
        <p class="signin-form__alt-methods">
          <button
            type="button"
            class="auth-btn auth-btn--inline"
            :disabled="loginLoading || !isOnline"
            @click="useDifferentSignInMethod"
          >
            {{ t('account.authCard.differentSignInMethod') }}
          </button>
        </p>
        <form class="signin-form__stack signin-form__stack--tight" @submit="onSubmitCode">
          <p
            v-if="loginCodeConfirmError"
            id="signin-code-feedback"
            class="auth-feedback auth-feedback--error"
            role="alert"
          >
            {{ translatedLoginCodeError(loginCodeConfirmError) }}
          </p>
          <div class="auth-field">
            <label for="signin-code" class="auth-label">{{
              t('account.authCard.loginCodeLabel')
            }}</label>
            <input
              id="signin-code"
              v-model="form.code"
              type="text"
              name="code"
              inputmode="numeric"
              autocomplete="one-time-code"
              :disabled="loginLoading || !isOnline"
              :aria-invalid="!!loginCodeConfirmError"
              :aria-describedby="loginCodeConfirmError ? 'signin-code-feedback' : undefined"
              class="auth-input"
            />
          </div>
          <div class="signin-form__cta-row">
            <router-link class="auth-btn auth-btn--outline signin-form__cancel" to="/">
              {{ t('account.authCard.cancel') }}
            </router-link>
            <button
              type="submit"
              class="auth-btn auth-btn--solid signin-form__submit"
              :disabled="loginLoading || !isOnline || !form.code.trim()"
            >
              {{ loginLoading ? t('account.authCard.verifying') : t('account.authCard.verifyAndSignIn') }}
            </button>
          </div>
        </form>
      </div>
    </template>
  </div>
</template>

<style scoped>
.signin-form {
  width: 100%;
}

.signin-form__stack {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.signin-form__stack--tight {
  gap: 1rem;
}

.signin-form__req {
  color: var(--color-error);
  font-weight: 600;
}

.signin-form__input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.signin-form__input-icon {
  position: absolute;
  left: 0.75rem;
  display: flex;
  color: var(--color-text-muted);
  pointer-events: none;
}

.signin-form__input--padded {
  padding-left: 2.75rem;
  padding-top: 0.65rem;
  padding-bottom: 0.65rem;
  min-height: 2.875rem;
  border-radius: 6px;
  background: var(--color-surface);
}

.signin-form__input--password {
  padding-right: 2.75rem;
}

.signin-form__toggle-pw {
  position: absolute;
  right: 0.35rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  transition:
    color 0.15s ease,
    background-color 0.15s ease;
}

.signin-form__toggle-pw:hover:not(:disabled) {
  color: var(--color-text);
  background: color-mix(in srgb, var(--color-bg-muted) 50%, transparent);
}

.signin-form__toggle-pw:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.signin-form__row-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding-top: 0.125rem;
}

.signin-form__remember {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: color 0.15s ease;
}

.signin-form__remember:hover {
  color: var(--color-text);
}

.signin-form__checkbox {
  width: 1rem;
  height: 1rem;
  border-radius: 3px;
  accent-color: var(--color-primary);
  cursor: pointer;
}

.signin-form__forgot {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-primary);
  text-decoration: none;
  transition: color 0.15s ease;
}

.signin-form__forgot:hover {
  color: color-mix(in srgb, var(--color-primary) 80%, black);
}

.signin-form__cta-row {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.75rem;
  padding-top: 0.25rem;
}

.signin-form__cancel {
  text-decoration: none;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  border-radius: 2px;
}

.signin-form__submit {
  flex: 1 1 10rem;
  min-height: 2.875rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  border-radius: 2px;
}

.signin-form__muted {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--color-text-muted);
}

.signin-form__callout {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid var(--color-border);
}

.signin-form__callout--info {
  background: color-mix(in srgb, #2563eb 6%, var(--color-surface));
  border-color: color-mix(in srgb, #2563eb 22%, var(--color-border));
  color: #1e3a5f;
}

.signin-form__callout--sent {
  background: color-mix(in srgb, var(--color-primary) 8%, var(--color-surface));
  border-color: color-mix(in srgb, var(--color-primary) 22%, var(--color-border));
}

.signin-form__callout-icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
  color: #2563eb;
}

.signin-form__callout-icon--primary {
  color: var(--color-primary);
}

.signin-form__callout-title {
  margin: 0 0 0.25rem;
  font-size: 0.875rem;
  font-weight: 600;
}

.signin-form__callout-title--primary {
  color: var(--color-primary);
}

.signin-form__callout-body {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--color-text-muted);
}

.signin-form__callout--info .signin-form__callout-body {
  color: #1e3a5f;
}

.signin-form__mfa-passkey-btn {
  margin-top: 0.75rem;
  width: 100%;
  min-height: 2.5rem;
}

.signin-form__alt-methods {
  margin: 0;
  font-size: 0.875rem;
  text-align: center;
}
</style>
